/**
 * AdminFreedrIQResponses — /admin/freedriq-responses
 *
 * All FreedrIQ submissions across the platform. Feeds the pre-launch
 * onboarding audit — spot-check what the incoming skill distribution
 * looks like and confirm every new user is completing the gate.
 */
import React, { useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { AuthContext } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { RefreshCw, BarChart3, Users } from 'lucide-react';

const scoreBand = (s) => {
  if (s == null) return 'text-muted-foreground';
  if (s >= 85) return 'text-primary';
  if (s >= 70) return 'text-primary';
  if (s >= 50) return 'text-amber-600';
  if (s >= 25) return 'text-amber-600';
  return 'text-emerald-600';
};

const AdminFreedrIQResponses = () => {
  const { API } = useContext(AuthContext);
  const [rows, setRows] = useState([]);
  const [distinctUsers, setDistinctUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [phase, setPhase] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 500 };
      if (phase !== 'all') params.phase = phase;
      const { data } = await axios.get(`${API}/admin/freedriq/responses`, { params });
      setRows(data?.rows || []);
      setDistinctUsers(data?.distinct_users || 0);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load responses');
    } finally {
      setLoading(false);
    }
  }, [API, phase]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-4 md:px-6 py-8" data-testid="admin-freedriq-responses-page">
        <div className="flex items-start justify-between gap-4 mb-6">
          <div>
            <div className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-primary" />
              <div className="text-[10px] uppercase tracking-widest font-bold text-primary">Onboarding audit</div>
            </div>
            <h1 className="font-heading text-3xl md:text-4xl font-black text-foreground leading-tight mt-1">
              FreedrIQ responses
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Every FreedrIQ submission — sorted newest first. Pre/post attempts, per user.
            </p>
          </div>
          <Button variant="outline" onClick={load} data-testid="refresh-freedriq-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-xs uppercase tracking-widest font-black text-muted-foreground">Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-heading text-3xl font-black" data-testid="stat-submissions">{rows.length}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-xs uppercase tracking-widest font-black text-muted-foreground">Distinct users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="font-heading text-3xl font-black flex items-center gap-2" data-testid="stat-users">
                <Users className="w-6 h-6 text-primary" /> {distinctUsers}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="pb-1">
              <CardTitle className="text-xs uppercase tracking-widest font-black text-muted-foreground">Phase filter</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex gap-1">
                {['all', 'pre', 'post'].map((p) => (
                  <Button
                    key={p}
                    size="sm"
                    variant={phase === p ? 'default' : 'outline'}
                    onClick={() => setPhase(p)}
                    className="rounded-full text-xs h-7 px-3 uppercase tracking-widest"
                    data-testid={`phase-filter-${p}`}
                  >
                    {p}
                  </Button>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Responses</CardTitle>
            <CardDescription>Newest first · {rows.length} rows</CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            {loading && <div className="p-6 text-center text-muted-foreground text-sm">Loading…</div>}
            {!loading && rows.length === 0 && (
              <div className="p-8 text-center text-muted-foreground text-sm" data-testid="freedriq-empty">
                No submissions yet.
              </div>
            )}
            {!loading && rows.map((r) => {
              const scoreVal = typeof r.score === 'object' ? (r.score?.score ?? null) : r.score;
              const confVal = typeof r.confidence === 'object' ? (r.confidence?.score ?? null) : r.confidence;
              return (
              <div
                key={r.id || `${r.user_id}-${r.completed_at}`}
                className="p-4 border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                data-testid={`freedriq-row-${r.id || r.user_id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-heading font-black text-sm truncate">{r.user_email || r.user_id}</span>
                      <Badge variant="outline" className="uppercase text-[10px] tracking-widest">
                        {r.tier || 'free'} · {r.phase || 'pre'}
                      </Badge>
                    </div>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {r.completed_at && new Date(r.completed_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="text-right shrink-0 flex items-center gap-4">
                    <div>
                      <div className={`font-heading text-2xl font-black ${scoreBand(scoreVal)}`}>
                        {scoreVal != null ? scoreVal : '—'}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Score</div>
                    </div>
                    <div>
                      <div className="font-heading text-2xl font-black">
                        {confVal != null ? confVal : '—'}
                      </div>
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Confidence</div>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminFreedrIQResponses;
